/**
 * TrackingClient — WebSocket viewer connection with automatic reconnection.
 *
 * Dispatches:
 *   'tracking'          — event.data is the decoded tracking frame
 *   'connectionchange'  — event.connected is true/false
 *
 * Wire protocol: binary "VT" frames from the bridge (see bridge.js encodeFrame).
 * Falls back to legacy JSON for compatibility.
 */

// ── Binary decode tables ──────────────────────────────────────────────────────
// MUST stay in the same order as BONE_NAMES / BLEND_NAMES in bridge.js

const BONE_NAMES = [
  'Hips',          'Spine',           'Chest',          'UpperChest',
  'Neck',          'Head',
  'LeftShoulder',  'LeftUpperArm',    'LeftLowerArm',   'LeftHand',
  'RightShoulder', 'RightUpperArm',   'RightLowerArm',  'RightHand',
  'LeftUpperLeg',  'LeftLowerLeg',    'LeftFoot',       'LeftToes',
  'RightUpperLeg', 'RightLowerLeg',   'RightFoot',      'RightToes',
  'LeftEye',       'RightEye',
  // Fingers — left
  'LeftThumbProximal',      'LeftThumbIntermediate',  'LeftThumbDistal',
  'LeftIndexProximal',      'LeftIndexIntermediate',  'LeftIndexDistal',
  'LeftMiddleProximal',     'LeftMiddleIntermediate', 'LeftMiddleDistal',
  'LeftRingProximal',       'LeftRingIntermediate',   'LeftRingDistal',
  'LeftLittleProximal',     'LeftLittleIntermediate', 'LeftLittleDistal',
  // Fingers — right
  'RightThumbProximal',     'RightThumbIntermediate', 'RightThumbDistal',
  'RightIndexProximal',     'RightIndexIntermediate', 'RightIndexDistal',
  'RightMiddleProximal',    'RightMiddleIntermediate','RightMiddleDistal',
  'RightRingProximal',      'RightRingIntermediate',  'RightRingDistal',
  'RightLittleProximal',    'RightLittleIntermediate','RightLittleDistal',
]

const BLEND_NAMES = [
  'A', 'I', 'U', 'E', 'O',
  'Neutral', 'Blink', 'Blink_L', 'Blink_R',
  'Joy', 'Angry', 'Sorrow', 'Fun',
  'LookUp', 'LookDown', 'LookLeft', 'LookRight',
]

/**
 * Decode a binary "VT" frame (ArrayBuffer) into the same plain object
 * that the old JSON path produced.  Returns null on bad magic.
 * @param {ArrayBuffer} buf
 */
function decodeFrame(buf) {
  const v = new DataView(buf)
  if (v.byteLength < 3) return null
  if (v.getUint8(0) !== 0x56 || v.getUint8(1) !== 0x54) return null   // magic "VT"

  const flags    = v.getUint8(2)
  const hasRoot  = (flags & 0x01) !== 0
  const hasBones = (flags & 0x02) !== 0
  const hasBlend = (flags & 0x04) !== 0

  const frame = { type: 'tracking', root: null, bones: {}, blendShapes: {} }
  let p = 3

  if (hasRoot) {
    const x  = v.getFloat32(p, true); p += 4
    const y  = v.getFloat32(p, true); p += 4
    const z  = v.getFloat32(p, true); p += 4
    const qx = v.getFloat32(p, true); p += 4
    const qy = v.getFloat32(p, true); p += 4
    const qz = v.getFloat32(p, true); p += 4
    const qw = v.getFloat32(p, true); p += 4
    frame.root = { x, y, z, qx, qy, qz, qw }
  }

  if (hasBones) {
    const count = v.getUint8(p++)
    for (let i = 0; i < count; i++) {
      const idx  = v.getUint8(p++)
      const x    = v.getFloat32(p, true); p += 4
      const y    = v.getFloat32(p, true); p += 4
      const z    = v.getFloat32(p, true); p += 4
      const qx   = v.getFloat32(p, true); p += 4
      const qy   = v.getFloat32(p, true); p += 4
      const qz   = v.getFloat32(p, true); p += 4
      const qw   = v.getFloat32(p, true); p += 4
      const name = BONE_NAMES[idx]
      if (name) frame.bones[name] = { x, y, z, qx, qy, qz, qw }
    }
  }

  if (hasBlend) {
    const count = v.getUint8(p++)
    for (let i = 0; i < count; i++) {
      const idx   = v.getUint8(p++)
      const value = v.getFloat32(p, true); p += 4
      const name  = BLEND_NAMES[idx]
      if (name) frame.blendShapes[name] = value
    }
  }

  return frame
}

// ── TrackingClient ────────────────────────────────────────────────────────────

export class TrackingClient extends EventTarget {
  #ws = null
  #url
  #reconnectTimer = null
  #reconnectDelay = 1000   // starts at 1 s, backs off up to 12 s
  #connected = false
  #stopped = false

  constructor(url) {
    super()
    this.#url = url
  }

  get connected() { return this.#connected }

  connect() {
    this.#stopped = false
    this.#clearTimer()
    this.#open()
  }

  disconnect() {
    this.#stopped = true
    this.#clearTimer()
    if (this.#ws) {
      this.#ws.onclose = null   // suppress the reconnect path
      this.#ws.close()
      this.#ws = null
    }
    this.#emit(false)
  }

  setUrl(url) {
    this.#url = url
    if (!this.#stopped) {
      this.disconnect()
      this.connect()
    }
  }

  // ── Private ────────────────────────────────────────────────────────────────

  #open() {
    this.#ws = new WebSocket(this.#url)
    // Receive binary frames as ArrayBuffer (not Blob) for zero-copy decode
    this.#ws.binaryType = 'arraybuffer'

    this.#ws.onopen = () => {
      this.#reconnectDelay = 1000
      // Identify as viewer — server will then relay streamer frames to us
      this.#ws.send(JSON.stringify({ type: 'identify', role: 'viewer' }))
      this.#emit(true)
    }

    this.#ws.onmessage = ({ data }) => {
      // ── Binary frame (new optimised bridge) ──
      if (data instanceof ArrayBuffer) {
        const frame = decodeFrame(data)
        if (frame) {
          const ev = new Event('tracking')
          ev.data = frame
          this.dispatchEvent(ev)
        }
        return
      }

      // ── Legacy JSON (old bridge / fallback) ──
      try {
        const msg = JSON.parse(data)
        if (msg.type === 'tracking') {
          const ev = new Event('tracking')
          ev.data = msg
          this.dispatchEvent(ev)
        }
      } catch { /* ignore malformed frames */ }
    }

    this.#ws.onclose = () => {
      this.#emit(false)
      if (!this.#stopped) this.#scheduleReconnect()
    }

    this.#ws.onerror = () => { /* onclose fires immediately after */ }
  }

  #scheduleReconnect() {
    this.#reconnectTimer = setTimeout(() => {
      if (!this.#stopped) this.#open()
    }, this.#reconnectDelay)
    this.#reconnectDelay = Math.min(this.#reconnectDelay * 1.5, 12_000)
  }

  #clearTimer() {
    if (this.#reconnectTimer !== null) {
      clearTimeout(this.#reconnectTimer)
      this.#reconnectTimer = null
    }
  }

  #emit(connected) {
    if (this.#connected === connected) return
    this.#connected = connected
    const ev = new Event('connectionchange')
    ev.connected = connected
    this.dispatchEvent(ev)
  }
}
